import type { IDistrictSchoolListItem } from "@/providers/store/api/districts/districts.interface";
import { SchoolLevelType } from "@/providers/store/api/schools/schools.interface";

export type DistrictSchoolsByType = Record<
  SchoolLevelType.K8 | SchoolLevelType.ELEMENTARY | SchoolLevelType.MIDDLE | SchoolLevelType.HIGH,
  IDistrictSchoolListItem[]
>;

const SCHOOL_TYPE_SECTION_ORDER = [
  SchoolLevelType.K8,
  SchoolLevelType.ELEMENTARY,
  SchoolLevelType.MIDDLE,
  SchoolLevelType.HIGH,
] as const;

type SchoolTypeSection = (typeof SCHOOL_TYPE_SECTION_ORDER)[number];

function createEmptyGroups(): DistrictSchoolsByType {
  return {
    [SchoolLevelType.K8]: [],
    [SchoolLevelType.ELEMENTARY]: [],
    [SchoolLevelType.MIDDLE]: [],
    [SchoolLevelType.HIGH]: [],
  };
}

function isSchoolTypeSection(type: string): type is SchoolTypeSection {
  return (SCHOOL_TYPE_SECTION_ORDER as readonly string[]).includes(type);
}

export function groupDistrictSchoolsByType(
  schools: readonly IDistrictSchoolListItem[] | null | undefined,
): DistrictSchoolsByType {
  const groups = createEmptyGroups();

  if (!schools?.length) {
    return groups;
  }

  const seenBySection = new Map<SchoolTypeSection, Set<string>>();

  for (const school of schools) {
    const types = school.schoolTypes ?? [];
    for (const rawType of types) {
      const type = String(rawType);
      if (!isSchoolTypeSection(type)) {
        continue;
      }

      const seen = seenBySection.get(type) ?? new Set<string>();
      if (seen.has(school.id)) {
        continue;
      }

      seen.add(school.id);
      seenBySection.set(type, seen);
      groups[type].push(school);
    }
  }

  for (const type of SCHOOL_TYPE_SECTION_ORDER) {
    groups[type].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }

  return groups;
}

export function filterDistrictSchoolsByName(
  schools: readonly IDistrictSchoolListItem[],
  query: string,
): IDistrictSchoolListItem[] {
  if (query.length === 0) {
    return [...schools];
  }

  const normalized = query.toLowerCase().trim();
  if (normalized.length === 0) {
    return [...schools];
  }

  return schools.filter((school) => (school.name ?? "").toLowerCase().includes(normalized));
}

export { SCHOOL_TYPE_SECTION_ORDER };
